import { HttpErrorResponse, HttpEvent, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { Acp4Api } from '../acp4-api/acp4-api.service';
import { AuthApi } from '../auth-api/auth-api.service';
import { DownloadFileService } from '../download-file/download-file.service';

/**
 * pdfSrcService transforms and assigns the file binaries to the
 * pdf viewer source so that it can be rendered in any component
 */
@Injectable({
  providedIn: 'root',
})
export class PdfViewerSrcManagerService {
  pdfSrc = new BehaviorSubject<Uint8Array | null>(null);
  pdfProxy: any = null;

  constructor(
    private apiSvc: Acp4Api,
    private authApi: AuthApi,
    private downloadService: DownloadFileService,
  ) {}

  /**
   * Request PDF binary for the pdf viewer to show,
   * depending on the ID passed in, the appropriate form should be fetched
   * @param DocumentTypeID to identify which form to fetch
   */
  getPdfDocumentTypeBinary(DocumentTypeID: number) {
    this.downloadService
      .requestDownload(this.apiSvc.URL_DOWNLOAD_FORM_BLOB, {
        DocumentTypeID,
      })
      .subscribe({
        next: evt => this.handleGetBinaryResponse(evt),
        error: err => this.apiSvc.defaultErrResHandler(err),
      });
  }

  /**
   * getPdfTemplateBinary() will send an API request to fetch template binary
   * by a specific ID and show general error if the request is unsuccessful - this
   * also happens if the ID doesn't exist in the database thus it's difficult to
   * separate this case from other 500 level errors
   * @param StagingTemplateID is the template ID to get it from DB
   */
  getPdfTemplateBinary(StagingTemplateID: number) {
    this.downloadService
      .requestDownload(this.apiSvc.URL_GET_TEMPLATE_FILE_BINARY, {
        SystemID: this.authApi.SystemID,
        StagingTemplateID,
      })
      .subscribe({
        next: evt => this.handleGetBinaryResponse(evt),
        error: err => this.apiSvc.defaultErrResHandler(err),
      });
  }

  /**
   * setPdfSourceFile() will convert the file or blob to uint8 and set it as pdf source
   * for viewer
   * @param file is an uploaded file or a blob
   * !NOTE: A File object is an instance of Blob but a Blob object is not an instance of File
   */
  async setPdfSourceFile(file: File | Blob) {
    this.pdfSrc.next(await this.transformBlobToUint8(file));
  }

  /**
   * Extract the PDF binary and update the source for the viewer to render
   * @param downloadEvents is the data sent back from the server that should contain binary array
   */
  handleGetBinaryResponse(
    downloadEvents: HttpErrorResponse | HttpResponse<Blob> | HttpEvent<any>,
  ): void {
    if (!(downloadEvents instanceof HttpResponse)) return;
    const blob: Blob = downloadEvents.body;
    void this.setPdfSourceFile(blob);
  }

  /**
   * Pdf viewer requires an Uint8Array, received blob needs to be converted
   * @param blob is a blob received from download or orther service
   * @returns Promise that gets resolved to Uint8Array
   */
  async transformBlobToUint8(blob: Blob): Promise<Uint8Array> {
    return new Uint8Array(await blob?.arrayBuffer());
  }

  /**
   * resetSource() to remove the binary from the memory - useful when
   * subscribing component runs onDestroy lifecycle hook
   */
  resetSource() {
    this.pdfSrc.next(null);
  }
}
